import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FormBreadCrumbSection } from "../FormBreadCrumbSection";
import { FormNavigationSection } from "../FormNavigationSection";
import {
  useGetGuaranteeOnboardingFormQuery,
  useSaveProfileGuaranteeOnboardingFormMutation,
  useSaveScoresGuaranteeOnboardingFormMutation,
} from "services/onboardingFormsAPI";
import { SectionType } from "utils/types/OnboardingForms";
import { Spinner } from "components/atoms/Icon";
import { FetchError } from "components/molecules";
import { RendererProfileInfo } from "../FormQuestionRenderer/RendererProfileInfo";
import { RendererScoreInfo } from "../FormQuestionRenderer/RendererScoreInfo";
import { profileFormInputsValidation } from "./profileFormInputsValidation";
import { scoreFormInputsValidation } from "./scoreFormInputsValidation";
import { RootState, store } from "state/store";
import { decodeToken } from "utils/functions/decodeToken";
import { SELF_PACED_PROGRAME } from "utils/constants";
import "./index.scss";

export const OnboardingFormSection = () => {
  const token = (store.getState() as RootState).auth.token;

  const payload = decodeToken();

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const [activeBreadcrumb, setActiveBreadcrumb] = useState(0);

  const [answerMapping, setAnswerMapping] = useState<any>({});

  const { isLoading, isError, data } = useGetGuaranteeOnboardingFormQuery();

  const [saveProfileForm, { isLoading: isLoadingProfileForm }]: any =
    useSaveProfileGuaranteeOnboardingFormMutation();

  const [
    saveScoreForm,
    { isLoading: isLoadingScoreForm, error: responseStatusScoreForm },
  ]: any = useSaveScoresGuaranteeOnboardingFormMutation();

  const handleAnswerChange = (id: string, value: any, type?: string) => {
    setAnswerMapping((prevData: any) => {
      if (type === "checkbox" || type === "String[]") {
        const currentValues = prevData[id] || [];
        const newValues = currentValues.includes(value.Value)
          ? currentValues.filter((item: any) => item !== value.Value)
          : [...currentValues, value.Value];
        return {
          ...prevData,
          [id]: newValues,
        };
      } else {
        return {
          ...prevData,
          [id]: value,
        };
      }
    });
  };

  const handleClickContinue = async (formNumber: number) => {
    if (formNumber === 0) {
      try {
        await saveProfileForm({
          GraduationYear: parseInt(answerMapping["GraduationYear"]),
          SchoolName: answerMapping["SchoolName"],
          ContactPreferenceList: answerMapping["ContactPreferenceList"],
          HasTimeAccommodation: answerMapping["HasTimeAccommodation"],
          TimeAccommodation: answerMapping["TimeAccommodation"],
          HasOtherOfficialAccommodation:
            answerMapping["HasOtherOfficialAccommodation"],
          ...(answerMapping.hasOwnProperty("OtherOfficialAccommodation") &&
            answerMapping["OtherOfficialAccommodation"] !== null && {
              OtherOfficialAccommodation:
                answerMapping["OtherOfficialAccommodation"],
            }),
          ...(answerMapping.hasOwnProperty("OtherTimeAccommodation") &&
            answerMapping["OtherTimeAccommodation"] !== null && {
              OtherTimeAccommodation: answerMapping["OtherTimeAccommodation"],
            }),
        })
          .unwrap()
          .then(() => {
            setActiveBreadcrumb((prev) => prev + 1);
          });
      } catch (error: any) {
        console.error(error);
      }
    } else {
      try {
        const formData = new FormData();

        formData.append(
          "HasTakenOfficialTest",
          answerMapping["HasTakenOfficialTest"]
        );
        if (answerMapping["HasTakenOfficialTest"] === true) {
          formData.append(
            "HasOfficialTestScore",
            answerMapping["HasOfficialTestScore"]
          );
          if (answerMapping["HasOfficialTestScore"] === true) {
            formData.append(
              "OfficialTestScore.ExamDate",
              dayjs(answerMapping["OfficialTestScore.ExamDate"])
                .format("MM/DD/YYYY")
                .toString()
            );
            formData.append(
              "OfficialTestScore.Components",
              JSON.stringify(
                answerMapping["OfficialTestScore.Components"].map(
                  (answer: any) => ({ Name: answer.Name, Value: answer.Value })
                )
              )
            );
            formData.append(
              "GoalScore.Components",
              JSON.stringify(
                answerMapping["GoalScore.Components"].map((answer: any) => ({
                  Name: answer.Name,
                  Value: answer.Value,
                }))
              )
            );
            for (
              let i = 0;
              i < answerMapping["OfficialTestFiles"].length;
              i++
            ) {
              formData.append(
                "OfficialTestFiles",
                answerMapping["OfficialTestFiles"][i]
              );
            }
            formData.append(
              "HasFutureOfficialTestAfterCourseEnds",
              answerMapping["HasFutureOfficialTestAfterCourseEnds"]
            );
            if (
              answerMapping["HasFutureOfficialTestAfterCourseEnds"] === true
            ) {
              formData.append(
                "FutureOfficialTestAfterCourseEndsDate",
                dayjs(answerMapping["FutureOfficialTestAfterCourseEndsDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestAfterCourseEndsDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestAfterCourseEndsAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            } else {
              formData.append(
                "FutureOfficialTestTargetTestDate",
                dayjs(answerMapping["FutureOfficialTestTargetTestDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestTargetTestDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestTargetTestDateAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            }
          } else {
            formData.append(
              "GoalScore.Components",
              JSON.stringify(
                answerMapping["GoalScore.Components"].map((answer: any) => ({
                  Name: answer.Name,
                  Value: answer.Value,
                }))
              )
            );
            formData.append(
              "HasFutureOfficialTestAfterCourseEnds",
              answerMapping["HasFutureOfficialTestAfterCourseEnds"]
            );
            if (
              answerMapping["HasFutureOfficialTestAfterCourseEnds"] === true
            ) {
              formData.append(
                "FutureOfficialTestAfterCourseEndsDate",
                dayjs(answerMapping["FutureOfficialTestAfterCourseEndsDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestAfterCourseEndsDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestAfterCourseEndsAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            } else {
              formData.append(
                "FutureOfficialTestTargetTestDate",
                dayjs(answerMapping["FutureOfficialTestTargetTestDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestTargetTestDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestTargetTestDateAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            }
          }
        } else {
          formData.append(
            "GoalScore.Components",
            JSON.stringify(
              answerMapping["GoalScore.Components"].map((answer: any) => ({
                Name: answer.Name,
                Value: answer.Value,
              }))
            )
          );
          formData.append(
            "HasFutureOfficialTestBeforeCourseEnds",
            answerMapping["HasFutureOfficialTestBeforeCourseEnds"]
          );
          if (answerMapping["HasFutureOfficialTestBeforeCourseEnds"] === true) {
            formData.append(
              "FutureOfficialTestBeforeCourseEndsDate",
              dayjs(answerMapping["FutureOfficialTestBeforeCourseEndsDate"])
                .format("MM/DD/YYYY")
                .toString()
            );
            formData.append(
              "HasFutureOfficialTestAfterCourseEnds",
              answerMapping["HasFutureOfficialTestAfterCourseEnds"]
            );
            if (
              answerMapping["HasFutureOfficialTestAfterCourseEnds"] === true
            ) {
              formData.append(
                "FutureOfficialTestAfterCourseEndsDate",
                dayjs(answerMapping["FutureOfficialTestAfterCourseEndsDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestAfterCourseEndsDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestAfterCourseEndsAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            } else {
              formData.append(
                "FutureOfficialTestTargetTestDate",
                dayjs(answerMapping["FutureOfficialTestTargetTestDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestTargetTestDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestTargetTestDateAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            }
          } else {
            formData.append(
              "HasFutureOfficialTestAfterCourseEnds",
              answerMapping["HasFutureOfficialTestAfterCourseEnds"]
            );
            if (
              answerMapping["HasFutureOfficialTestAfterCourseEnds"] === true
            ) {
              formData.append(
                "FutureOfficialTestAfterCourseEndsDate",
                dayjs(answerMapping["FutureOfficialTestAfterCourseEndsDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestAfterCourseEndsDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestAfterCourseEndsAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            } else {
              formData.append(
                "FutureOfficialTestTargetTestDate",
                dayjs(answerMapping["FutureOfficialTestTargetTestDate"])
                  .format("MM/DD/YYYY")
                  .toString()
              );
              if (
                dayjs(
                  answerMapping["FutureOfficialTestTargetTestDate"]
                ).isAfter(data?.TargetTestDate.TestDate)
              ) {
                formData.append(
                  "InvalidOfficialTestTargetTestDateAcknowledgement",
                  answerMapping[
                    "InvalidOfficialTestTargetTestDateAcknowledgement"
                  ]
                );
              }
            }
          }
        }

        await saveScoreForm(formData)
          .unwrap()
          .then(() => {
            // navigate to dashboard on success
            window.location.href = `https://${process.env.REACT_APP_HOSTED_URL}/${isSelfPaced ? `self-paced` : ""}?token=${token}`;
          });
      } catch (error: any) {
        console.error(error);
      }
    }
  };

  const handleClickLater = () => {
    // navigate to jump page
    window.location.href = `${process.env.REACT_APP_RETURN_URL}`;
  };

  useEffect(() => {
    data?.Sections[0].Questions.forEach((val: any) => {
      if (
        val.SubQuestions &&
        val.SubQuestions[0] &&
        val.SubQuestions[0].SubQuestions &&
        val.SubQuestions[0].SubQuestions[0].Value !== null
      ) {
        setAnswerMapping((prevData: any) => {
          return {
            ...prevData,
            [val.SubQuestions[0].SubQuestions[0].FieldName]:
              val.SubQuestions[0].SubQuestions[0].Value,
          };
        });
      }
      if (val.SubQuestions && val.SubQuestions[0].Value !== null) {
        setAnswerMapping((prevData: any) => {
          return {
            ...prevData,
            [val.SubQuestions[0].FieldName]: val.SubQuestions[0].Value,
          };
        });
      }
      if (val.Value !== null) {
        setAnswerMapping((prevData: any) => {
          return {
            ...prevData,
            [val.FieldName]: val.Value,
          };
        });
      }
    });
  }, [data]);

  return (
    <div className="onboarding-form-section-wrapper">
      {isLoading ? (
        <Spinner theme="light" />
      ) : isError ? (
        <FetchError content="Oops! failed to load resources." />
      ) : (
        <>
          <FormBreadCrumbSection
            activeItemIndex={activeBreadcrumb}
            breadCrumbItems={data?.Sections.map(
              (item: SectionType, index: number) =>
                `${index + 1}. ${item.DisplayName}`
            )}
          />
          <div className="questions-section-wrapper">
            {activeBreadcrumb === 0 ? (
              <RendererProfileInfo
                questions={data?.Sections[0].Questions}
                allAnswers={answerMapping}
                onAnswerChange={handleAnswerChange}
              />
            ) : null}
            {activeBreadcrumb === 1 ? (
              <RendererScoreInfo
                questions={data?.Sections[1].Questions}
                errorResponse={responseStatusScoreForm}
                allAnswers={answerMapping}
                onAnswerChange={handleAnswerChange}
                testDate={data?.TargetTestDate.TestDate}
              />
            ) : null}
          </div>
          <FormNavigationSection
            isDisableContinue={
              activeBreadcrumb === 0
                ? !profileFormInputsValidation(answerMapping)
                : !scoreFormInputsValidation(
                    data?.Sections[1].Questions,
                    answerMapping,
                    data?.TargetTestDate.TestDate
                  )
            }
            handleContinue={() => handleClickContinue(activeBreadcrumb)}
            handleLater={handleClickLater}
            isLoading={isLoadingProfileForm || isLoadingScoreForm}
          />
        </>
      )}
    </div>
  );
};
